import { useState } from 'react';
import { pictureOf } from '../content/images';

export type IllustrationSize = 'scene' | 'phrase' | 'cue' | 'thumb' | 'avatar';

/** How wide each size is drawn, in CSS px, so the browser picks the right file for the screen. */
const SIZES: Record<IllustrationSize, string> = {
  scene: '(min-width: 640px) 576px, calc(100vw - 32px)',
  phrase: '200px',
  cue: '152px',
  thumb: '72px',
  avatar: '32px',
};

interface Props {
  id: string;
  size: IllustrationSize;
  /** Hidden from screen readers when the words next to it already say everything. */
  decorative?: boolean;
  /** Load at once (the picture the screen is about), rather than when it scrolls into view. */
  priority?: boolean;
  className?: string;
}

/**
 * One of the course's illustrations: a transparent cut-out (outlined on the dark theme by the
 * filter below) or an opaque scene with rounded corners. Space is reserved for it before it
 * loads, and it fades in, so nothing jumps. Renders nothing for an id with no picture yet.
 */
export function Illustration({ id, size, decorative = false, priority = false, className }: Props) {
  const picture = pictureOf(id);
  const [loaded, setLoaded] = useState(false);
  if (!picture) return null;
  return (
    <div className={`illo illo-${size}${className ? ` ${className}` : ''}`} data-loaded={loaded || undefined} data-opaque={picture.opaque || undefined}>
      <img
        src={picture.src}
        srcSet={`${picture.small} ${picture.smallWidth}w, ${picture.src} ${picture.width}w`}
        sizes={SIZES[size]}
        width={picture.width}
        height={picture.height}
        alt={decorative ? '' : picture.alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
}

export function hasIllustration(id: string): boolean {
  return pictureOf(id) !== null;
}

/**
 * The dark theme's outline for cut-outs, rendered once by the app: a thin paper-coloured line
 * grown only from the fully opaque part of the picture, so it never shows through the soft fade
 * at the bottom of a figure (a CSS drop-shadow did, turning every fade into a grey band).
 */
export function IllustrationDefs() {
  return (
    <svg width="0" height="0" aria-hidden="true" focusable="false" style={{ position: 'absolute' }}>
      <filter id="illo-rim" x="-2%" y="-2%" width="104%" height="104%" colorInterpolationFilters="sRGB">
        <feComponentTransfer in="SourceAlpha" result="core">
          <feFuncA type="linear" slope="20" intercept="-19" />
        </feComponentTransfer>
        <feMorphology in="core" operator="dilate" radius="1" result="grown" />
        <feFlood floodColor="#f6f2ea" floodOpacity="0.85" />
        <feComposite in2="grown" operator="in" result="grownRim" />
        {/* Only outside the figure: never under its own semi-transparent pixels. */}
        <feComposite in="grownRim" in2="SourceAlpha" operator="out" result="rim" />
        <feColorMatrix in="SourceGraphic" type="matrix" values="0.92 0 0 0 0  0 0.92 0 0 0  0 0 0.92 0 0  0 0 0 1 0" result="dim" />
        <feMerge>
          <feMergeNode in="rim" />
          <feMergeNode in="dim" />
        </feMerge>
      </filter>
    </svg>
  );
}
